<template>
    <div class="modal fade show modal-style" aria-labelledby="makeDeliveryTitle">
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" ref="modalRef">
            <div class="modal-content">
                <div class="d-flex justify-content-between px-2 my-1 align-items-center">
                    <h4 class="mb-0">Make Delivery — Create Invoice</h4>
                    <button
                        @click="onClickClose"
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                    ></button>
                </div>

                <hr class="mt-0 mb-1">

                <Loader v-if="loading" />

                <div v-if="!loading" class="px-2 pb-2">
                    <div class="row gy-1 bg-blue-light p-1 rounded">
                        <div class="col-12 col-md-3">
                            <label class="form-label">Customer Name</label>
                            <input type="text" class="form-control" :value="customerName" readonly>
                        </div>
                        <div class="col-12 col-md-3">
                            <label class="form-label">Order No</label>
                            <input type="text" class="form-control" :value="orderNo" readonly>
                        </div>
                        <div class="col-12 col-md-2">
                            <label class="form-label">Invoice Date</label>
                            <input type="date" class="form-control" v-model="invoiceDate">
                        </div>
                        <div class="col-12 col-md-2">
                            <label class="form-label">Order Type</label>
                            <input type="text" class="form-control" :value="orderDetail.sale_type" readonly>
                        </div>
                        <div class="col-12 col-md-2">
                            <label class="form-label">Ship from (Warehouse)</label>
                            <input type="text" class="form-control" :value="warehouseName" readonly>
                        </div>
                    </div>

                    <div class="table-responsive mt-2">
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th>SL</th>
                                    <th>Product Name</th>
                                    <th>Stock Balance</th>
                                    <th class="text-end">Qty</th>
                                    <th class="text-end">Rate</th>
                                    <th class="text-end">Total Amount</th>
                                    <th class="text-end">Free QTY</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in itemDetails" :key="index">
                                    <td>{{ index + 1 }}</td>
                                    <td>{{ item.product_name }}</td>
                                    <td>{{ item.stock }}</td>
                                    <td class="text-end">{{ item.quantity }}</td>
                                    <td class="text-end">{{ item.rate }}</td>
                                    <td class="text-end">{{ lineTotal(item) }}</td>
                                    <td class="text-end">{{ item.free_quantity || 0 }}</td>
                                </tr>
                                <tr v-if="!hasItems">
                                    <td colspan="7" class="text-center text-muted">No items found.</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="row justify-content-between mt-2">
                        <div class="col-12 col-md-5">
                            <div class="mb-1">
                                <label class="form-label">Mode Of Payment</label>
                                <v-select
                                    placeholder="Select Cash & Bank Account"
                                    class="w-100"
                                    v-model="modeOfPayment"
                                    :options="cashAndBank"
                                    label="name"
                                    :reduce="name => name.id"
                                />
                                <span v-if="isSubmitted && !modeOfPayment" class="text-danger">
                                    <small>Mode of payment is required</small>
                                </span>
                            </div>
                            <div class="mb-1">
                                <label class="form-label">Paid Amount</label>
                                <input type="number" class="form-control text-end" :value="grandTotal" disabled>
                            </div>
                        </div>
                        <div class="col-12 col-md-4">
                            <div class="mb-1 row">
                                <div class="col-sm-5"><label class="col-form-label">Sub total</label></div>
                                <div class="col-sm-7"><input :value="subTotal" readonly type="text" class="form-control text-end"></div>
                            </div>
                            <div class="mb-1 row">
                                <div class="col-sm-5"><label class="col-form-label">VAT</label></div>
                                <div class="col-sm-7"><input :value="totalVAT" readonly type="text" class="form-control text-end"></div>
                            </div>
                            <div class="mb-1 row">
                                <div class="col-sm-5"><label class="col-form-label">Net Bill</label></div>
                                <div class="col-sm-7"><input :value="grandTotal" readonly type="text" class="form-control text-end"></div>
                            </div>
                        </div>
                    </div>

                    <div class="d-flex justify-content-end gap-1 mt-2">
                        <button @click="onClickClose" class="btn btn-outline-secondary">Cancel</button>
                        <button
                            :disabled="saving || !modeOfPayment"
                            @click="onSubmit"
                            class="btn btn-primary"
                        >Create Invoice</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useRoute } from 'vue-router'
import Loader from '@/components/atom/LoaderComponent'
import handlePurchase from '@/services/modules/purchase'
import handleReceipt from '@/services/modules/receipt'
import handleDropdowns from '@/services/modules/dropdowns'
import handleBusinessesLocations from '@/services/modules/businessesLocations'

const props = defineProps({
    order: { type: Object, required: true }
})

const emit = defineEmits(['close', 'created'])

const route = useRoute()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const { fetchSalesOrderById, storeBillPayment, getAccountHeadBySlag } = handlePurchase()
const { fetchCashAndBank } = handleReceipt()
const { fetchDropdownList } = handleDropdowns()
const { fetchBusinessLocationsList } = handleBusinessesLocations()

const loading = ref(false)
const saving = ref(false)
const isSubmitted = ref(false)
const cashAndBank = ref([])
const modeOfPayment = ref(null)
const invoiceDate = ref(new Date().toISOString().split('T')[0])
const itemDetails = ref([])
const orderDetail = ref({})
const locations = ref([])
const accountDefaults = ref({
    account_head_id: null,
    vat_payable_account_head_id: null,
    discount_head_id: null,
    vat_challan_account_head_id: null,
    ait_challan_account_head_id: null,
    discount_account_head_id: null
})

const companyId = computed(() => route.params.companyId)

const customerName = computed(() => {
    if (!props.order.party_name) return ''
    return props.order.party_name
})

const orderNo = computed(() => {
    if (orderDetail.value.bill_number) return orderDetail.value.bill_number
    if (orderDetail.value.order_number) return orderDetail.value.order_number
    return ''
})

const warehouseName = computed(() => {
    const match = locations.value.find(item => item.id === orderDetail.value.location_id)
    if (!match) return ''
    return match.text
})

const hasItems = computed(() => itemDetails.value.length > 0)

const lineTotal = (item) => {
    const amount = (item.quantity * item.rate) - (item.discount_amount || 0)
    return amount.toFixed(2)
}

const subTotal = computed(() => {
    let sum = 0
    itemDetails.value.forEach(item => {
        sum += (item.quantity * item.rate) - (item.discount_amount || 0)
    })
    return sum.toFixed(2)
})

const totalVAT = computed(() => {
    let vat = 0
    itemDetails.value.forEach(item => {
        if (item.vat_amount) vat += parseFloat(item.vat_amount)
    })
    return vat.toFixed(2)
})

const grandTotal = computed(() => (parseFloat(subTotal.value) + parseFloat(totalVAT.value)).toFixed(2))

const firstId = (list) => {
    if (!list || !list.length) return null
    return list[0].id
}

const getDropDownQuery = () => {
    return `?company_id=${companyId.value}`
        + `&is_account_heads=1`
        + `&is_all_account_heads=1`
        + `&slags[]=sales_accounts`
        + `&slags[]=accounts_receivable`
        + `&slags[]=vat_payable`
}

const getAccountDefaults = async () => {
    const res = await fetchDropdownList(getDropDownQuery())
    if (!res.status || !res.data) return
    const bySlag = res.data.account_head_list_by_slag
    if (!bySlag) return
    accountDefaults.value.account_head_id = firstId(bySlag.accounts_receivable)
    accountDefaults.value.vat_payable_account_head_id = firstId(bySlag.vat_payable)
    accountDefaults.value.discount_head_id = firstId(bySlag.sales_accounts)
}

const getChallanDefaults = async () => {
    const companyQuery = `?company_id=${companyId.value}`
    const [vatRes, aitRes, discountRes] = await Promise.all([
        getAccountHeadBySlag('vat_challan', companyQuery),
        getAccountHeadBySlag('ait_challan', companyQuery),
        getAccountHeadBySlag('discount_account', companyQuery)
    ])
    accountDefaults.value.vat_challan_account_head_id = firstId(vatRes.data)
    accountDefaults.value.ait_challan_account_head_id = firstId(aitRes.data)
    accountDefaults.value.discount_account_head_id = firstId(discountRes.data)
}

const getCashAndBank = async () => {
    const res = await fetchCashAndBank(companyId.value)
    if (!res.data) return
    cashAndBank.value = res.data
    modeOfPayment.value = firstId(res.data)
}

const getLocations = async () => {
    const res = await fetchBusinessLocationsList(`?company_id=${companyId.value}`)
    if (!res.data) return
    locations.value = res.data
}

const getOrderDetail = async () => {
    const res = await fetchSalesOrderById(props.order.id, `?company_id=${companyId.value}`)
    if (!res.data) return showError('Order information not found.')
    orderDetail.value = res.data
    if (!res.data.general) return
    itemDetails.value = res.data.general.map(item => ({
        ...item,
        product_name: item.product ? item.product.name : ''
    }))
}

const buildPayload = () => {
    return {
        company_id: companyId.value,
        contact_profile_id: props.order.contact_profile_id,
        sales_order_master_id: props.order.id,
        location_id: props.order.location_id,
        account_head_id: accountDefaults.value.account_head_id,
        vat_payable_account_head_id: accountDefaults.value.vat_payable_account_head_id,
        discount_head_id: accountDefaults.value.discount_head_id,
        vat_challan_account_head_id: accountDefaults.value.vat_challan_account_head_id,
        ait_challan_account_head_id: accountDefaults.value.ait_challan_account_head_id,
        discount_account_head_id: accountDefaults.value.discount_account_head_id,
        mop_account_head_id: modeOfPayment.value,
        payment: grandTotal.value,
        has_item_detail: 1,
        status: 'active',
        sale_type: orderDetail.value.sale_type,
        date: invoiceDate.value,
        adjustment_amount: 0,
        advance_amount: 0,
        commission_amount: 0,
        sale_master_receipt: true,
        order_source: 'sales_order_ecommerce',
        order_status: 'delivered',
        item_details: itemDetails.value.filter(item => Number(item.quantity) > 0)
    }
}

const onSubmit = async () => {
    isSubmitted.value = true
    if (!modeOfPayment.value) return showError('Please select mode of payment')
    saving.value = true
    try {
        const res = await storeBillPayment(buildPayload())
        saving.value = false
        if (!res.status) return showError(res.message)
        showSuccess(res.message)
        emit('created')
    } catch (err) {
        saving.value = false
        showError('Something went wrong, please try again.')
    }
}

const onClickClose = () => {
    emit('close')
}

onMounted(async () => {
    loading.value = true
    await Promise.all([
        getCashAndBank(),
        getOrderDetail(),
        getAccountDefaults(),
        getChallanDefaults(),
        getLocations()
    ])
    loading.value = false
})
</script>

<style scoped>
.modal-style {
    display: block;
    background: #1c1d1c33;
}
</style>
